import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Trash2, ArrowLeft, Save, Image as ImageIcon, UtensilsCrossed,
  MessageCircleQuestion, Settings2, Check, Upload, Film, Loader2, X,
  GripVertical, CalendarClock, Copy, ArrowDownWideNarrow,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { eventsApi } from "@/lib/api";
import { SlugField } from "@/components/SlugField";
import { COVER_PRESETS, ITINERARY_TEMPLATES } from "@/lib/helpers";

const EMPTY = {
  title: "",
  slug: "",
  host_name: "",
  event_type: "Wedding",
  date: "",
  end_date: "",
  time: "",
  end_time: "",
  venue_name: "",
  address: "",
  description: "",
  cover_image: COVER_PRESETS[0].url,
  gallery: [],
  schedule: [],
  show_attendees: true,
  allow_plus_ones: true,
  max_party_size: 2,
  rsvp_deadline: "",
  meal_enabled: true,
  meal_options: [],
  custom_questions: [],
};

const EVENT_TYPES = ["Wedding", "Corporate Gala", "Birthday", "Dinner Party", "Celebration", "Conference", "Baby Shower", "Anniversary"];

const Field = ({ label, children, required }) => (
  <label className="block">
    <span className="text-sm font-medium">{label}{required && <span className="text-gold"> *</span>}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

const inputCls = "w-full rounded-xl border border-input bg-card px-4 py-2.5 text-sm outline-none transition-colors focus:border-gold focus:ring-2 focus:ring-gold/20";

export default function EventEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [savedSlug, setSavedSlug] = useState("");
  const [coverUploading, setCoverUploading] = useState(false);
  const [galleryUploading, setGalleryUploading] = useState(false);

  useEffect(() => {
    if (editing) {
      eventsApi.get(id).then((e) => { setForm({ ...EMPTY, ...e }); setSavedSlug(e.slug); }).catch(() => {
        toast.error("Event not found");
        navigate("/dashboard");
      });
    }
  }, [id, editing, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadCover = async (file) => {
    if (!file) return;
    setCoverUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await eventsApi.uploadMedia(fd);
      if (res.type !== "image") {
        toast.error("Cover must be an image. Add videos to the gallery below.");
      } else {
        set("cover_image", res.url);
        toast.success("Cover photo uploaded");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cover upload failed");
    } finally {
      setCoverUploading(false);
    }
  };

  const uploadGallery = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setGalleryUploading(true);
    try {
      const uploaded = [];
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await eventsApi.uploadMedia(fd);
        uploaded.push({ id: res.id, url: res.url, type: res.type, filename: res.filename });
      }
      setForm((f) => ({ ...f, gallery: [...(f.gallery || []), ...uploaded] }));
      toast.success(`${uploaded.length} file${uploaded.length > 1 ? "s" : ""} added`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setGalleryUploading(false);
    }
  };

  const removeMedia = (mid) => set("gallery", (form.gallery || []).filter((m) => m.id !== mid));

  const dragIdx = useRef(null);
  const onDragStart = (i) => { dragIdx.current = i; };
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (i) => {
    const from = dragIdx.current;
    if (from === null || from === i) return;
    const arr = [...(form.gallery || [])];
    const [moved] = arr.splice(from, 1);
    arr.splice(i, 0, moved);
    set("gallery", arr);
    dragIdx.current = null;
  };

  const addScheduleItem = () => set("schedule", [...(form.schedule || []), { time: "", title: "", location: "", description: "", notes: "" }]);
  const updateScheduleItem = (i, k, v) => set("schedule", form.schedule.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const removeScheduleItem = (i) => set("schedule", form.schedule.filter((_, idx) => idx !== i));
  const duplicateScheduleItem = (i) => {
    const arr = [...form.schedule];
    arr.splice(i + 1, 0, { ...form.schedule[i] });
    set("schedule", arr);
  };
  const loadTemplate = (key) => {
    const tpl = ITINERARY_TEMPLATES[key];
    if (!tpl) return;
    set("schedule", [...(form.schedule || []), ...tpl.map((t) => ({ ...t, day: "" }))]);
    toast.success(`${key} template added`);
  };

  const parseTime = (t) => {
    if (!t) return Number.MAX_SAFE_INTEGER;
    const m = t.trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i);
    if (!m) return Number.MAX_SAFE_INTEGER;
    let h = parseInt(m[1], 10);
    const min = m[2] ? parseInt(m[2], 10) : 0;
    const ap = m[3] ? m[3].toLowerCase() : null;
    if (ap === "pm" && h < 12) h += 12;
    if (ap === "am" && h === 12) h = 0;
    return h * 60 + min;
  };
  const sortScheduleByTime = () => {
    set("schedule", [...(form.schedule || [])].sort((a, b) => parseTime(a.time) - parseTime(b.time)));
    toast.success("Sorted chronologically");
  };

  const schedDrag = useRef(null);
  const onSchedDragStart = (i) => { schedDrag.current = i; };
  const onSchedDrop = (i) => {
    const from = schedDrag.current;
    if (from === null || from === i) return;
    const arr = [...(form.schedule || [])];
    const [moved] = arr.splice(from, 1);
    arr.splice(i, 0, moved);
    set("schedule", arr);
    schedDrag.current = null;
  };

  const addMeal = () => set("meal_options", [...form.meal_options, { label: "", description: "", dietary_tag: "" }]);
  const updateMeal = (i, k, v) => set("meal_options", form.meal_options.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)));
  const removeMeal = (i) => set("meal_options", form.meal_options.filter((_, idx) => idx !== i));

  const addQ = () => set("custom_questions", [...form.custom_questions, { label: "", type: "text", options: [], required: false }]);
  const updateQ = (i, k, v) => set("custom_questions", form.custom_questions.map((q, idx) => (idx === i ? { ...q, [k]: v } : q)));
  const removeQ = (i) => set("custom_questions", form.custom_questions.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Please give your event a title");
      return;
    }
    if (!form.date) {
      toast.error("Event start date is required");
      return;
    }
    if (form.end_date && form.end_date < form.date) {
      toast.error("Event end date cannot be before the start date");
      return;
    }
    if (!form.venue_name.trim()) {
      toast.error("Event venue is required");
      return;
    }
    if (form.slug && (form.slug.length < 3 || !/^[a-z0-9]+(-[a-z0-9]+)*$/.test(form.slug))) {
      toast.error("Event URL can use only lowercase letters, numbers and single hyphens (min 3)");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        end_date: form.end_date || form.date,
        max_party_size: parseInt(form.max_party_size, 10) || 1,
        meal_options: form.meal_options.filter((m) => m.label.trim()),
        custom_questions: form.custom_questions.filter((q) => q.label.trim()),
      };
      let res;
      if (editing) {
        res = await eventsApi.update(id, payload);
        toast.success("Event updated");
      } else {
        res = await eventsApi.create(payload);
        toast.success("Event created");
      }
      navigate(`/events/${res.id}/manage`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save event");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow text-gold">{editing ? "Edit event" : "Create event"}</p>
            <h1 className="mt-2 font-serif text-3xl font-semibold tracking-tight sm:text-4xl">
              {editing ? "Refine the details" : "Design your event"}
            </h1>
          </div>
          <button
            data-testid="event-save-btn"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-all hover:shadow-lg disabled:opacity-60"
          >
            <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save Event"}
          </button>
        </div>

        {/* Basics */}
        <section className="mt-8 rounded-2xl border border-border bg-card p-6 sm:p-8">
          <div className="flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-gold" />
            <h2 className="text-lg font-semibold">Event basics</h2>
          </div>
          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Field label="Event title" required>
                <input data-testid="event-title-input" className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Victoria & Alexander's Wedding" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <SlugField value={form.slug || ""} onChange={(v) => set("slug", v)} title={form.title} eventId={editing ? id : ""} savedSlug={savedSlug} inputCls={inputCls} />
            </div>
            <Field label="Host name(s)">
              <input data-testid="event-host-input" className={inputCls} value={form.host_name} onChange={(e) => set("host_name", e.target.value)} placeholder="The Thompson Family" />
            </Field>
            <Field label="Event type">
              <select data-testid="event-type-select" className={inputCls} value={form.event_type} onChange={(e) => set("event_type", e.target.value)}>
                {EVENT_TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Event Start Date" required>
              <input data-testid="event-date-input" type="date" className={inputCls} value={form.date} onChange={(e) => set("date", e.target.value)} />
            </Field>
            <Field label="Event End Date">
              <input data-testid="event-end-date-input" type="date" className={inputCls} value={form.end_date} min={form.date || undefined} onChange={(e) => set("end_date", e.target.value)} />
              <p className="mt-1 text-xs text-muted-foreground">Leave blank (or same as start) for a single-day event.</p>
            </Field>
            <Field label="Start Time (optional)">
              <input data-testid="event-time-input" type="time" className={inputCls} value={form.time} onChange={(e) => set("time", e.target.value)} />
              <p className="mt-1 text-xs text-muted-foreground">Optional — leave blank if the event has no set start time.</p>
            </Field>
            <Field label="End Time (optional)">
              <input data-testid="event-end-time-input" type="time" className={inputCls} value={form.end_time} onChange={(e) => set("end_time", e.target.value)} />
              <p className="mt-1 text-xs text-muted-foreground">Optional — leave blank if the event has no set end time.</p>
            </Field>
            <Field label="Venue name" required>
              <input data-testid="event-venue-input" className={inputCls} value={form.venue_name} onChange={(e) => set("venue_name", e.target.value)} placeholder="The Grand Ballroom" />
            </Field>
            <Field label="Address">
              <input data-testid="event-location-input" className={inputCls} value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="1 Estate Drive, Napa Valley, CA" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Description">
                <textarea data-testid="event-description-input" rows={4} className={inputCls} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Tell your guests what to expect..." />
              </Field>
            </div>
            <Field label="RSVP deadline">
              <input data-testid="event-deadline-input" type="date" className={inputCls} value={form.rsvp_deadline} onChange={(e) => set("rsvp_deadline", e.target.value)} />
            </Field>
          </div>
        </section>

        {/* Cover */}
        <section className="mt-6 rounded-2xl border border-border bg-card p-6 sm:p-8">
          <div className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-gold" />
            <h2 className="text-lg font-semibold">Cover image</h2>
          </div>

          {/* current cover preview + device upload */}
          <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="h-28 w-full overflow-hidden rounded-xl border border-border bg-cover bg-center sm:w-48" style={{ backgroundImage: `url(${form.cover_image})` }} />
            <label data-testid="cover-upload-label" className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-all hover:shadow-lg">
              {coverUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {coverUploading ? "Uploading..." : "Upload from device"}
              <input type="file" accept="image/*" className="hidden" disabled={coverUploading} onChange={(e) => uploadCover(e.target.files[0])} />
            </label>
          </div>

          <p className="mt-6 text-sm font-medium text-muted-foreground">Or choose a preset</p>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="event-cover-select">
            {COVER_PRESETS.map((c) => (
              <button
                key={c.id}
                onClick={() => set("cover_image", c.url)}
                className={`relative overflow-hidden rounded-xl border-2 transition-all ${form.cover_image === c.url ? "border-gold ring-2 ring-gold/30" : "border-transparent hover:border-border"}`}
              >
                <div className="h-24 w-full bg-cover bg-center" style={{ backgroundImage: `url(${c.url})` }} />
                {form.cover_image === c.url && (
                  <span className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-gold text-primary">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                )}
              </button>
            ))}
          </div>
        </section>

        {/* Gallery */}
        <section className="mt-6 rounded-2xl border border-border bg-card p-6 sm:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Film className="h-5 w-5 text-gold" />
              <h2 className="text-lg font-semibold">Photo & video gallery</h2>
            </div>
            <label data-testid="gallery-upload-label" className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-semibold transition-colors hover:bg-secondary">
              {galleryUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {galleryUploading ? "Uploading..." : "Upload photos / videos"}
              <input type="file" accept="image/*,video/*" multiple className="hidden" disabled={galleryUploading} onChange={(e) => uploadGallery(e.target.files)} />
            </label>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">Upload directly from your device. Drag tiles to reorder. These appear on the public event page. Images up to 20MB, videos up to 100MB.</p>

          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4" data-testid="gallery-grid">
            {(form.gallery || []).length === 0 && !galleryUploading && (
              <p className="col-span-full text-sm text-muted-foreground">No media yet.</p>
            )}
            {(form.gallery || []).map((m, mi) => (
              <div
                key={m.id}
                data-testid={`gallery-item-${m.id}`}
                draggable
                onDragStart={() => onDragStart(mi)}
                onDragOver={onDragOver}
                onDrop={() => onDrop(mi)}
                className="group relative aspect-square cursor-move overflow-hidden rounded-xl border border-border bg-secondary"
              >
                {m.type === "video" ? (
                  <video src={m.url} className="h-full w-full object-cover" muted />
                ) : (
                  <img src={m.url} alt={m.filename} className="h-full w-full object-cover" />
                )}
                <span className="absolute left-2 bottom-2 flex h-6 w-6 items-center justify-center rounded-md bg-black/60 text-white opacity-0 transition-opacity group-hover:opacity-100">
                  <GripVertical className="h-4 w-4" />
                </span>
                {m.type === "video" && (
                  <span className="absolute left-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-xs font-medium text-white">
                    <Film className="h-3 w-3" /> Video
                  </span>
                )}
                <button
                  onClick={() => removeMedia(m.id)}
                  data-testid={`remove-media-${m.id}`}
                  className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity hover:bg-destructive group-hover:opacity-100"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
            {galleryUploading && (
              <div className="flex aspect-square items-center justify-center rounded-xl border border-dashed border-border">
                <Loader2 className="h-6 w-6 animate-spin text-gold" />
              </div>
            )}
          </div>
        </section>

        {/* Schedule / Itinerary */}
        <section className="mt-6 rounded-2xl border border-border bg-card p-6 sm:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-gold" />
              <h2 className="text-lg font-semibold">Schedule & itinerary</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              <select
                data-testid="template-picker"
                value=""
                onChange={(e) => { if (e.target.value) { loadTemplate(e.target.value); e.target.value = ""; } }}
                className="rounded-full border border-border bg-card px-4 py-2 text-sm font-medium outline-none transition-colors hover:bg-secondary"
              >
                <option value="">Use a template…</option>
                {Object.keys(ITINERARY_TEMPLATES).map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
              {(form.schedule || []).length > 1 && (
                <button data-testid="sort-schedule-btn" onClick={sortScheduleByTime} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-secondary">
                  <ArrowDownWideNarrow className="h-4 w-4" /> Sort by time
                </button>
              )}
              <button data-testid="add-schedule-btn" onClick={addScheduleItem} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90">
                <Plus className="h-4 w-4" /> Add item
              </button>
            </div>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">Build a timeline for any event — drag to reorder. Set a day on each item for multi-day events (weddings, festivals). Shown on the public event page.</p>
          <div className="mt-5 space-y-3">
            {(form.schedule || []).length === 0 && <p className="text-sm text-muted-foreground">No schedule items yet. Add your first.</p>}
            {(form.schedule || []).map((s, i) => (
              <div
                key={i}
                data-testid={`schedule-row-${i}`}
                draggable
                onDragStart={() => onSchedDragStart(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onSchedDrop(i)}
                className="rounded-2xl border border-border bg-secondary/30 p-4"
              >
                <div className="flex items-start gap-3">
                  <span className="mt-2.5 cursor-move text-muted-foreground" title="Drag to reorder"><GripVertical className="h-4 w-4" /></span>
                  <div className="grid flex-1 gap-3 sm:grid-cols-[0.7fr_1.3fr_1fr]">
                    <input className={inputCls} placeholder="4:00 PM" value={s.time || ""} onChange={(e) => updateScheduleItem(i, "time", e.target.value)} data-testid={`schedule-time-${i}`} />
                    <input className={inputCls} placeholder="Activity (e.g. Wedding Ceremony)" value={s.title || ""} onChange={(e) => updateScheduleItem(i, "title", e.target.value)} data-testid={`schedule-title-${i}`} />
                    <input className={inputCls} placeholder="Location (e.g. Main Lawn)" value={s.location || ""} onChange={(e) => updateScheduleItem(i, "location", e.target.value)} data-testid={`schedule-location-${i}`} />
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => duplicateScheduleItem(i)} data-testid={`schedule-duplicate-${i}`} title="Duplicate" className="flex h-9 w-9 items-center justify-center rounded-lg border border-border transition-colors hover:bg-secondary">
                      <Copy className="h-4 w-4" />
                    </button>
                    <button onClick={() => removeScheduleItem(i)} data-testid={`schedule-delete-${i}`} title="Delete" className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-destructive transition-colors hover:bg-destructive/10">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <div className="mt-3 grid gap-3 pl-7 sm:grid-cols-[0.8fr_1fr_1fr]">
                  <div>
                    <input type="date" className={inputCls} value={s.day || ""} onChange={(e) => updateScheduleItem(i, "day", e.target.value)} data-testid={`schedule-day-${i}`} title="Day (for multi-day events)" />
                    <span className="mt-1 block text-xs text-muted-foreground">Day (optional)</span>
                  </div>
                  <input className={inputCls} placeholder="Description (optional)" value={s.description || ""} onChange={(e) => updateScheduleItem(i, "description", e.target.value)} data-testid={`schedule-desc-${i}`} />
                  <input className={inputCls} placeholder="Additional notes (optional)" value={s.notes || ""} onChange={(e) => updateScheduleItem(i, "notes", e.target.value)} data-testid={`schedule-notes-${i}`} />
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* RSVP settings */}
        <section className="mt-6 rounded-2xl border border-border bg-card p-6 sm:p-8">
          <h2 className="text-lg font-semibold">RSVP settings</h2>
          <div className="mt-6 space-y-5">
            <label className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium">Show who's attending</p>
                <p className="text-sm text-muted-foreground">Display a public guest list on the event page (first name + last initial only).</p>
              </div>
              <button
                data-testid="show-attendees-toggle"
                onClick={() => set("show_attendees", !form.show_attendees)}
                className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${form.show_attendees ? "bg-gold" : "bg-muted"}`}
              >
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${form.show_attendees ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </label>
            <label className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium">Allow plus-ones</p>
                <p className="text-sm text-muted-foreground">Let guests bring companions.</p>
              </div>
              <button
                data-testid="allow-plusones-toggle"
                onClick={() => set("allow_plus_ones", !form.allow_plus_ones)}
                className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${form.allow_plus_ones ? "bg-gold" : "bg-muted"}`}
              >
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${form.allow_plus_ones ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </label>
            {form.allow_plus_ones && (
              <Field label="Maximum party size (per guest)">
                <input data-testid="max-party-input" type="number" min={1} max={10} className={`${inputCls} max-w-[140px]`} value={form.max_party_size} onChange={(e) => set("max_party_size", e.target.value)} />
              </Field>
            )}
            <label className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium">Collect meal choices</p>
                <p className="text-sm text-muted-foreground">Offer a menu for attendees to choose from.</p>
              </div>
              <button
                data-testid="meal-enabled-toggle"
                onClick={() => set("meal_enabled", !form.meal_enabled)}
                className={`relative h-6 w-11 rounded-full transition-colors ${form.meal_enabled ? "bg-gold" : "bg-muted"}`}
              >
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${form.meal_enabled ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </label>
          </div>
        </section>

        {/* Meals */}
        {form.meal_enabled && (
          <section className="mt-6 rounded-2xl border border-border bg-card p-6 sm:p-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UtensilsCrossed className="h-5 w-5 text-gold" />
                <h2 className="text-lg font-semibold">Meal options</h2>
              </div>
              <button data-testid="add-meal-btn" onClick={addMeal} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-secondary">
                <Plus className="h-4 w-4" /> Add meal
              </button>
            </div>
            <div className="mt-5 space-y-3">
              {form.meal_options.length === 0 && <p className="text-sm text-muted-foreground">No meals yet. Add dishes for your guests to choose from.</p>}
              {form.meal_options.map((m, i) => (
                <div key={i} data-testid={`meal-row-${i}`} className="grid gap-3 rounded-xl bg-secondary/40 p-4 sm:grid-cols-[1.3fr_1.3fr_0.8fr_auto]">
                  <input className={inputCls} placeholder="Dish name" value={m.label} onChange={(e) => updateMeal(i, "label", e.target.value)} />
                  <input className={inputCls} placeholder="Short description" value={m.description} onChange={(e) => updateMeal(i, "description", e.target.value)} />
                  <input className={inputCls} placeholder="Tag (V, Vegan...)" value={m.dietary_tag} onChange={(e) => updateMeal(i, "dietary_tag", e.target.value)} />
                  <button onClick={() => removeMeal(i)} className="flex items-center justify-center rounded-xl border border-border px-3 text-destructive transition-colors hover:bg-destructive/10">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Custom questions */}
        <section className="mt-6 rounded-2xl border border-border bg-card p-6 sm:p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircleQuestion className="h-5 w-5 text-gold" />
              <h2 className="text-lg font-semibold">Custom questions</h2>
            </div>
            <button data-testid="add-question-btn" onClick={addQ} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-secondary">
              <Plus className="h-4 w-4" /> Add question
            </button>
          </div>
          <div className="mt-5 space-y-3">
            {form.custom_questions.length === 0 && <p className="text-sm text-muted-foreground">Ask anything — song requests, allergies, shuttle needs.</p>}
            {form.custom_questions.map((q, i) => (
              <div key={i} data-testid={`question-row-${i}`} className="rounded-xl bg-secondary/40 p-4">
                <div className="grid gap-3 sm:grid-cols-[1.6fr_0.9fr_auto_auto]">
                  <input className={inputCls} placeholder="Question label" value={q.label} onChange={(e) => updateQ(i, "label", e.target.value)} />
                  <select className={inputCls} value={q.type} onChange={(e) => updateQ(i, "type", e.target.value)}>
                    <option value="text">Text answer</option>
                    <option value="yesno">Yes / No</option>
                    <option value="datetime">Date &amp; Time</option>
                    <option value="choice">Multiple choice</option>
                    <option value="id_upload">Photo ID upload</option>
                  </select>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={q.required} onChange={(e) => updateQ(i, "required", e.target.checked)} className="h-4 w-4 accent-[hsl(var(--gold))]" />
                    Required
                  </label>
                  <button onClick={() => removeQ(i)} className="flex items-center justify-center rounded-xl border border-border px-3 text-destructive transition-colors hover:bg-destructive/10">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                {q.type === "choice" && (
                  <div className="mt-3">
                    <input
                      data-testid={`question-options-${i}`}
                      className={inputCls}
                      placeholder="Options, comma separated (e.g. Air, Train, Self Drive)"
                      value={(q.options || []).join(", ")}
                      onChange={(e) => updateQ(i, "options", e.target.value.split(",").map((o) => o.trim()).filter(Boolean))}
                    />
                    <p className="mt-1.5 text-xs text-muted-foreground">Guests pick one option. Example: "Mode of Arrival" → Air, Train, Self Drive.</p>
                  </div>
                )}
                {q.type === "id_upload" && (
                  <p className="mt-3 text-xs text-muted-foreground">Every attendee (guest + each plus-one) will be asked to upload a government photo ID (image or PDF). Files are stored securely.</p>
                )}
              </div>
            ))}
          </div>
        </section>

        <div className="mt-8 flex justify-end">
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3.5 text-sm font-semibold text-primary-foreground transition-all hover:shadow-lg disabled:opacity-60"
          >
            <Save className="h-4 w-4" /> {saving ? "Saving..." : "Save Event"}
          </button>
        </div>
      </div>
    </div>
  );
}
